import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { contact } from "@/data/contact";

/**
 * Persistent mobile bottom bar. Two equal-width tap targets:
 *   1. Call our team (tel: — company line, not a personal number)
 *   2. Contact (→ /contact)
 *
 * Layer rules:
 *   - z-index: 40 (assistant bubble is 50)
 *   - hidden on /admin/*, when assistant panel is open, when a form input is
 *     focused and mobile keyboard is up (detected via visualViewport shrink)
 *   - width < 768px only
 */
export default function MobileCallBar() {
  const { pathname } = useLocation();
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [kbOpen, setKbOpen] = useState(false);

  // Observe assistant panel open state via DOM (Assistant.jsx mounts a data-testid="assistant-panel" when open)
  useEffect(() => {
    const check = () => {
      setAssistantOpen(Boolean(document.querySelector("[data-testid='assistant-panel']")));
    };
    check();
    const mo = new MutationObserver(check);
    mo.observe(document.body, { childList: true, subtree: true });
    return () => mo.disconnect();
  }, []);

  // Detect mobile keyboard open via visualViewport height shrink
  useEffect(() => {
    if (typeof window === "undefined" || !window.visualViewport) return;
    const base = window.innerHeight;
    const onResize = () => {
      const shrink = base - window.visualViewport.height;
      setKbOpen(shrink > 150);
    };
    window.visualViewport.addEventListener("resize", onResize);
    return () => window.visualViewport.removeEventListener("resize", onResize);
  }, []);

  if (pathname.startsWith("/admin")) return null;
  if (assistantOpen) return null;
  if (kbOpen) return null;

  const cp = contact.company_phone;
  return (
    <div className="gp-callbar" data-testid="mobile-callbar" role="region" aria-label="Quick contact">
      <a
        href={`tel:${cp.tel}`}
        className="gp-callbar__btn"
        data-testid="mobile-callbar-call"
        aria-label={`Call our team on ${cp.raw}`}
      >
        <PhoneGlyph />
        <span>Call our team</span>
      </a>
      <Link
        to="/contact"
        className="gp-callbar__btn"
        data-testid="mobile-callbar-contact"
      >
        <MailGlyph />
        <span>Contact</span>
      </Link>
    </div>
  );
}

function PhoneGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"
        d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3.1-8.7A2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.3 1.8.6 2.6a2 2 0 0 1-.5 2.1L8 9.6a16 16 0 0 0 6 6l1.2-1.2a2 2 0 0 1 2.1-.5c.8.3 1.7.5 2.6.6a2 2 0 0 1 1.7 2z"/>
    </svg>
  );
}

function MailGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"
        d="M4 6h16v12H4z M4 6l8 7 8-7"/>
    </svg>
  );
}
